import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Send, Megaphone, Bell, Layers,
  AlertCircle, History, CheckCircle2, Plus, Pencil, Trash2,
  Search, X, FileText, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { supabase } from '@/services/supabase';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { PermissionGate } from '@/components/PermissionGate';

type BroadcastKind = 'push' | 'popup' | 'banner';

interface BroadcastRow {
  id: string;
  kind: BroadcastKind;
  title: string;
  body: string;
  cta_label: string | null;
  cta_action: string | null;
  audience: Audience;
  persistent: boolean;
  expires_at: string | null;
  sent_at: string | null;
  recipients_count: number;
  created_at: string;
}

interface Audience {
  all?:          boolean;
  roles?:        string[];
  user_ids?:     string[];
  account_ids?:  string[];
  plan_codes?:   string[];
}

interface PlanOption    { id: string; code: string; name: string; }
interface AccountOption { id: string; name: string; account_number: string | null; }

interface TemplateRow {
  id: string;
  name: string;
  description: string | null;
  kind: BroadcastKind;
  title: string;
  body: string;
  cta_label: string | null;
  cta_action: string | null;
  created_at: string;
  updated_at: string;
}

const ROLE_OPTIONS = ['client', 'patrol', 'operator', 'admin', 'super_admin'] as const;
const ROLE_LABEL: Record<string, string> = {
  client: 'Clientes', patrol: 'Patrulleros', operator: 'Operadores',
  admin: 'Administradores', super_admin: 'Super-admin',
};

const KIND_LABEL: Record<BroadcastKind, string> = {
  push:   'Push (notificación)',
  popup:  'Pop-up (modal)',
  banner: 'Banner (in-app)',
};

const KIND_ICON: Record<BroadcastKind, typeof Bell> = {
  push:   Bell,
  popup:  Megaphone,
  banner: Layers,
};

type Tab = 'compose' | 'history' | 'templates';

interface ComposeForm {
  kinds:       BroadcastKind[];
  title:       string;
  body:        string;
  cta_label:   string;
  cta_action:  string;
  persistent:  boolean;
  expires_at:  string;
  audience:    Audience;
}

const EMPTY_FORM: ComposeForm = {
  kinds:      ['push'],
  title:      '',
  body:       '',
  cta_label:  '',
  cta_action: '',
  persistent: false,
  expires_at: '',
  audience:   { all: true },
};

export function CommunicationsPage() {
  const [tab, setTab] = useState<Tab>('compose');
  const [broadcasts, setBroadcasts] = useState<BroadcastRow[]>([]);
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [prefillForm, setPrefillForm] = useState<Partial<ComposeForm> | null>(null);

  const loadBroadcasts = useCallback(async () => {
    const { data, error } = await supabase
      .from('user_broadcasts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) setError(error.message);
    else setBroadcasts((data ?? []) as BroadcastRow[]);
  }, []);

  const loadTemplates = useCallback(async () => {
    const { data, error } = await supabase
      .from('broadcast_templates')
      .select('*')
      .order('name', { ascending: true });
    if (error) setError(error.message);
    else setTemplates((data ?? []) as TemplateRow[]);
  }, []);

  const loadCatalogs = useCallback(async () => {
    const { data: planRows } = await supabase
      .from('subscription_plans')
      .select('id, code, name')
      .order('sort_order');
    setPlans((planRows ?? []) as PlanOption[]);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadBroadcasts(), loadTemplates(), loadCatalogs()]);
      setLoading(false);
    })();

    const channel = supabase
      .channel(`broadcasts-realtime-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_broadcasts' }, () => loadBroadcasts())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'broadcast_templates' }, () => loadTemplates())
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [loadBroadcasts, loadTemplates, loadCatalogs]);

  const useTemplate = (tpl: TemplateRow) => {
    setPrefillForm({
      kinds:      [tpl.kind],
      title:      tpl.title,
      body:       tpl.body,
      cta_label:  tpl.cta_label ?? '',
      cta_action: tpl.cta_action ?? '',
    });
    setTab('compose');
  };

  return (
    <div className="container mx-auto p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Megaphone className="size-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">Comunicaciones</h1>
          <p className="text-sm text-muted-foreground">
            Envía notificaciones push, pop-ups y banners a usuarios de la app.
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b overflow-x-auto">
        <TabBtn active={tab === 'compose'}   onClick={() => setTab('compose')}   label="Nuevo envío" />
        <TabBtn active={tab === 'templates'} onClick={() => setTab('templates')} label={`Plantillas (${templates.length})`} />
        <TabBtn active={tab === 'history'}   onClick={() => setTab('history')}   label={`Historial (${broadcasts.length})`} />
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="size-4" />
          <span>{error}</span>
        </div>
      )}

      {tab === 'compose' && (
        <PermissionGate require="communications.send">
          <ComposeBroadcast
            plans={plans}
            templates={templates}
            prefill={prefillForm}
            onPrefillConsumed={() => setPrefillForm(null)}
            onSent={() => { loadBroadcasts(); setTab('history'); }}
          />
        </PermissionGate>
      )}

      {tab === 'templates' && (
        <PermissionGate require="communications.send">
          <TemplatesPanel
            templates={templates}
            loading={loading}
            onReload={loadTemplates}
            onUse={useTemplate}
          />
        </PermissionGate>
      )}

      {tab === 'history' && (
        <HistoryList
          loading={loading}
          broadcasts={broadcasts}
          plans={plans}
          onDeleted={loadBroadcasts}
        />
      )}
    </div>
  );
}

// ─── Compose ────────────────────────────────────────────────────────

function ComposeBroadcast({
  plans, templates, prefill, onPrefillConsumed, onSent,
}: {
  plans: PlanOption[];
  templates: TemplateRow[];
  prefill: Partial<ComposeForm> | null;
  onPrefillConsumed: () => void;
  onSent: () => void;
}) {
  const [form, setForm] = useState<ComposeForm>(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, ...prefill }));
      onPrefillConsumed();
    }
  }, [prefill, onPrefillConsumed]);

  const setAudience = (patch: Partial<Audience>) => {
    setForm((f) => ({ ...f, audience: { ...f.audience, ...patch } }));
  };

  const toggleKind = (k: BroadcastKind) => {
    setForm((f) => {
      const has = f.kinds.includes(k);
      const next = has ? f.kinds.filter((x) => x !== k) : [...f.kinds, k];
      return { ...f, kinds: next.length ? next : [k] };
    });
  };

  const toggleRole = (role: string) => {
    const cur = new Set(form.audience.roles ?? []);
    if (cur.has(role)) cur.delete(role); else cur.add(role);
    setAudience({ roles: Array.from(cur), all: false });
  };

  const togglePlan = (code: string) => {
    const cur = new Set(form.audience.plan_codes ?? []);
    if (cur.has(code)) cur.delete(code); else cur.add(code);
    setAudience({ plan_codes: Array.from(cur), all: false });
  };

  const toggleAccount = (id: string) => {
    const cur = new Set(form.audience.account_ids ?? []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setAudience({ account_ids: Array.from(cur), all: false });
  };

  const setAccountIds = (ids: string[]) => {
    setAudience({ account_ids: ids, all: false });
  };

  const applyTemplate = (id: string) => {
    if (!id) return;
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    setForm((f) => ({
      ...f,
      kinds:      [tpl.kind],
      title:      tpl.title,
      body:       tpl.body,
      cta_label:  tpl.cta_label ?? '',
      cta_action: tpl.cta_action ?? '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);

    if (!form.title.trim() || !form.body.trim()) {
      setFeedback({ kind: 'err', msg: 'Título y mensaje son obligatorios.' });
      return;
    }
    if (form.kinds.length === 0) {
      setFeedback({ kind: 'err', msg: 'Selecciona al menos un canal.' });
      return;
    }

    setSending(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const createdBy = userData.user?.id ?? null;

      let totalSent = 0;
      const errors: string[] = [];

      for (const kind of form.kinds) {
        const { data: inserted, error: insErr } = await supabase
          .from('user_broadcasts')
          .insert({
            kind,
            title:      form.title.trim(),
            body:       form.body.trim(),
            cta_label:  form.cta_label.trim() || null,
            cta_action: form.cta_action.trim() || null,
            audience:   form.audience,
            persistent: kind === 'push' ? false : form.persistent,
            expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
            created_by: createdBy,
          })
          .select('id')
          .single();

        if (insErr || !inserted) {
          errors.push(`${KIND_LABEL[kind]}: ${insErr?.message ?? 'insert failed'}`);
          continue;
        }

        const { data: fnData, error: fnErr } = await supabase.functions.invoke('send-broadcast', {
          body: { broadcast_id: inserted.id },
        });

        if (fnErr) {
          errors.push(`${KIND_LABEL[kind]}: ${fnErr.message}`);
          continue;
        }
        totalSent += (fnData as { sent?: number })?.sent ?? 0;
      }

      if (errors.length) {
        setFeedback({ kind: 'err', msg: `Algunos envíos fallaron: ${errors.join(' | ')}` });
      } else {
        const channels = form.kinds.length;
        const perChannel = Math.round(totalSent / Math.max(1, channels));
        setFeedback({
          kind: 'ok',
          msg: channels > 1
            ? `Enviado: ${channels} canales × ${perChannel} destinatarios (${totalSent} total).`
            : `Enviado a ${totalSent} destinatario${totalSent === 1 ? '' : 's'}.`,
        });
        setForm(EMPTY_FORM);
        onSent();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setFeedback({ kind: 'err', msg });
    } finally {
      setSending(false);
    }
  };

  const audienceSummary = useMemo(
    () => describeAudience(form.audience, plans),
    [form.audience, plans],
  );

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <Card className="xl:col-span-2 min-w-0">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Mensaje</CardTitle>
          {templates.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <FileText className="size-4 text-muted-foreground" />
              <select
                onChange={(e) => { applyTemplate(e.target.value); e.target.value = ''; }}
                defaultValue=""
                className="rounded-md border border-input bg-background px-2 py-1 text-xs"
              >
                <option value="" disabled>Cargar plantilla…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
              Canales (puedes elegir varios)
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {(['push', 'popup', 'banner'] as BroadcastKind[]).map((k) => {
                const Icon = KIND_ICON[k];
                const active = form.kinds.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleKind(k)}
                    className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left text-sm transition ${
                      active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-accent'
                    }`}
                  >
                    <div className="flex w-full items-center gap-2 font-medium">
                      <input
                        type="checkbox"
                        readOnly
                        checked={active}
                        className="pointer-events-none"
                      />
                      <Icon className="size-4" />
                      {KIND_LABEL[k]}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {k === 'push'   && 'Notificación del sistema (FCM)'}
                      {k === 'popup'  && 'Modal al abrir la app'}
                      {k === 'banner' && 'Banner persistente en la app'}
                    </span>
                  </button>
                );
              })}
            </div>
            {form.kinds.length > 1 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Se creará un envío independiente para cada canal seleccionado.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Título</label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ej. Tu factura del mes ya está lista"
              maxLength={80}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Mensaje</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Texto del mensaje que verán los usuarios."
              rows={4}
              maxLength={500}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
                Botón (opcional)
              </label>
              <Input
                value={form.cta_label}
                onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
                placeholder="Ej. Ver factura"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
                Acción del botón
              </label>
              <Input
                value={form.cta_action}
                onChange={(e) => setForm((f) => ({ ...f, cta_action: e.target.value }))}
                placeholder="open_url:https://… o open_screen:Plans"
              />
            </div>
          </div>

          {(form.kinds.includes('popup') || form.kinds.includes('banner')) && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 rounded-md border border-dashed p-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.persistent}
                  onChange={(e) => setForm((f) => ({ ...f, persistent: e.target.checked }))}
                />
                <span>
                  <span className="font-medium">Persistente</span>
                  <span className="block text-xs text-muted-foreground">
                    Se vuelve a mostrar hasta que el usuario lo descarte.
                  </span>
                </span>
              </label>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
                  Expira (opcional)
                </label>
                <Input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="min-w-0">
        <CardHeader>
          <CardTitle className="text-base">Audiencia</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.audience.all}
              onChange={(e) => setAudience(
                e.target.checked
                  ? { all: true, roles: [], plan_codes: [], account_ids: [], user_ids: [] }
                  : { all: false },
              )}
              className="mt-1"
            />
            <span>
              <span className="font-medium">Todos los clientes</span>
              <span className="block text-xs text-muted-foreground">
                Solo titulares de cuenta (rol cliente). Para avisar a patrulleros u operadores, usa “Por rol”.
              </span>
            </span>
          </label>

          {!form.audience.all && (
            <>
              <div>
                <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">Por rol</div>
                <div className="flex flex-wrap gap-2">
                  {ROLE_OPTIONS.map((r) => {
                    const selected = form.audience.roles?.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => toggleRole(r)}
                        className={`rounded-full border px-3 py-1 text-xs transition ${
                          selected ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-accent'
                        }`}
                      >
                        {ROLE_LABEL[r]}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Tip: para forzar a los patrulleros a actualizar la app, envía un <strong>Pop-up</strong> al rol “Patrulleros” con el botón “Abrir Play Store”.
                </p>
              </div>

              <div>
                <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">Por plan</div>
                <div className="flex flex-wrap gap-2">
                  {plans.map((p) => {
                    const selected = form.audience.plan_codes?.includes(p.code);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePlan(p.code)}
                        className={`rounded-full border px-3 py-1 text-xs transition ${
                          selected ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-accent'
                        }`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                  {plans.length === 0 && (
                    <span className="text-xs text-muted-foreground">No hay planes configurados.</span>
                  )}
                </div>
              </div>

              <AccountPicker
                selectedIds={form.audience.account_ids ?? []}
                onChange={setAccountIds}
                onToggle={toggleAccount}
              />
            </>
          )}

          <div className="rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
            <strong className="block text-foreground">Resumen:</strong>
            {audienceSummary}
          </div>
        </CardContent>
      </Card>

      <div className="xl:col-span-3 flex flex-wrap items-center justify-between gap-3">
        {feedback ? (
          <div
            className={`flex items-center gap-2 text-sm ${
              feedback.kind === 'ok' ? 'text-emerald-600' : 'text-destructive'
            }`}
          >
            {feedback.kind === 'ok' ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
            <span>{feedback.msg}</span>
          </div>
        ) : <span />}
        <Button type="submit" disabled={sending}>
          {sending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Send className="mr-2 size-4" />}
          {sending
            ? 'Enviando…'
            : `Enviar ${form.kinds.length > 1 ? `(${form.kinds.length} canales)` : 'ahora'}`}
        </Button>
      </div>
    </form>
  );
}

// ─── Account picker with search ───────────────────────────────────

function AccountPicker({
  selectedIds, onChange, onToggle,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onToggle: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AccountOption[]>([]);
  const [selectedRows, setSelectedRows] = useState<AccountOption[]>([]);
  const [searching, setSearching] = useState(false);

  // Hydrate names for currently-selected ids.
  useEffect(() => {
    if (selectedIds.length === 0) {
      setSelectedRows([]);
      return;
    }
    const known = new Set(selectedRows.map((r) => r.id));
    const missing = selectedIds.filter((id) => !known.has(id));
    if (missing.length === 0) {
      setSelectedRows((rows) => rows.filter((r) => selectedIds.includes(r.id)));
      return;
    }
    void (async () => {
      const { data } = await supabase
        .from('accounts')
        .select('id, name, account_number')
        .in('id', missing);
      setSelectedRows((rows) => {
        const merged = [...rows, ...((data ?? []) as AccountOption[])];
        const seen = new Set<string>();
        return merged.filter((r) => {
          if (seen.has(r.id) || !selectedIds.includes(r.id)) return false;
          seen.add(r.id);
          return true;
        });
      });
    })();
  }, [selectedIds, selectedRows]);

  // Debounced search by name or account_number.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      const safe = q.replace(/[%,]/g, '');
      const { data } = await supabase
        .from('accounts')
        .select('id, name, account_number')
        .or(`name.ilike.%${safe}%,account_number.ilike.%${safe}%`)
        .eq('is_active', true)
        .limit(30);
      setResults((data ?? []) as AccountOption[]);
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const selectAllResults = () => {
    const next = Array.from(new Set([...selectedIds, ...results.map((r) => r.id)]));
    onChange(next);
  };

  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase text-muted-foreground">
        Por cuenta (buscar)
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        El envío llega al <strong>titular</strong> de cada cuenta seleccionada.
      </p>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre o número de cuenta…"
          className="pl-8"
        />
      </div>

      {query.trim().length >= 2 && (
        <div className="mt-2 max-h-48 overflow-auto rounded-md border p-2">
          {searching && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin" /> Buscando…
            </div>
          )}
          {!searching && results.length === 0 && (
            <span className="text-xs text-muted-foreground">Sin resultados.</span>
          )}
          {!searching && results.length > 0 && (
            <div className="mb-2 flex items-center justify-between border-b pb-1">
              <span className="text-xs text-muted-foreground">{results.length} resultado(s)</span>
              <button
                type="button"
                onClick={selectAllResults}
                className="text-xs font-medium text-primary hover:underline"
              >
                Seleccionar todos
              </button>
            </div>
          )}
          <div className="space-y-1">
            {results.map((a) => {
              const checked = selectedIds.includes(a.id);
              return (
                <label key={a.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={checked} onChange={() => onToggle(a.id)} />
                  <span className="min-w-0 flex-1 truncate">
                    {a.name}
                    {a.account_number && (
                      <span className="ml-1 text-xs text-muted-foreground">#{a.account_number}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Seleccionadas ({selectedIds.length})
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs text-destructive hover:underline"
            >
              Limpiar
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {selectedRows.map((a) => (
              <span
                key={a.id}
                className="inline-flex items-center gap-1 rounded-full border bg-primary/5 px-2 py-0.5 text-xs"
              >
                {a.name}
                {a.account_number && (
                  <span className="text-muted-foreground">#{a.account_number}</span>
                )}
                <button
                  type="button"
                  onClick={() => onToggle(a.id)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Quitar"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            {selectedRows.length < selectedIds.length && (
              <Loader2 className="size-3 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Templates panel (CRUD) ───────────────────────────────────────

const EMPTY_TPL = {
  id: '',
  name: '',
  description: '',
  kind: 'push' as BroadcastKind,
  title: '',
  body: '',
  cta_label: '',
  cta_action: '',
};

function TemplatesPanel({
  templates, loading, onReload, onUse,
}: {
  templates: TemplateRow[];
  loading: boolean;
  onReload: () => void;
  onUse: (tpl: TemplateRow) => void;
}) {
  const [editing, setEditing] = useState<typeof EMPTY_TPL | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.title.trim() || !editing.body.trim()) {
      setError('Nombre, título y mensaje son obligatorios.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      name:        editing.name.trim(),
      description: editing.description.trim() || null,
      kind:        editing.kind,
      title:       editing.title.trim(),
      body:        editing.body.trim(),
      cta_label:   editing.cta_label.trim() || null,
      cta_action:  editing.cta_action.trim() || null,
    };

    const { error } = editing.id
      ? await supabase.from('broadcast_templates').update(payload).eq('id', editing.id)
      : await supabase.from('broadcast_templates').insert(payload);

    setSaving(false);
    if (error) { setError(error.message); return; }
    setEditing(null);
    onReload();
  };

  const remove = async (id: string) => {
    if (!confirm('¿Eliminar esta plantilla?')) return;
    const { error } = await supabase.from('broadcast_templates').delete().eq('id', id);
    if (error) { setError(error.message); return; }
    onReload();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Plantillas reutilizables (por ejemplo, recordatorio de pago mensual).
        </p>
        <Button onClick={() => setEditing({ ...EMPTY_TPL })}>
          <Plus className="mr-2 size-4" /> Nueva plantilla
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 size-5 animate-spin" /> Cargando…
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-md border border-dashed py-12 text-center text-sm text-muted-foreground">
          Aún no hay plantillas. Crea una para reutilizarla cada mes.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((t) => {
            const Icon = KIND_ICON[t.kind];
            return (
              <Card key={t.id} className="flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base truncate">{t.name}</CardTitle>
                    <Badge variant="outline" className="shrink-0">
                      <Icon className="mr-1 size-3" />
                      {KIND_LABEL[t.kind]}
                    </Badge>
                  </div>
                  {t.description && (
                    <p className="text-xs text-muted-foreground">{t.description}</p>
                  )}
                </CardHeader>
                <CardContent className="flex flex-1 flex-col gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Título</div>
                    <div className="text-sm">{t.title}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Mensaje</div>
                    <div className="line-clamp-3 text-sm text-muted-foreground">{t.body}</div>
                  </div>
                  <div className="mt-auto flex flex-wrap gap-2 pt-2">
                    <Button size="sm" onClick={() => onUse(t)}>
                      <Send className="mr-1 size-3" /> Usar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({
                      id:          t.id,
                      name:        t.name,
                      description: t.description ?? '',
                      kind:        t.kind,
                      title:       t.title,
                      body:        t.body,
                      cta_label:   t.cta_label ?? '',
                      cta_action:  t.cta_action ?? '',
                    })}>
                      <Pencil className="mr-1 size-3" /> Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(t.id)}>
                      <Trash2 className="mr-1 size-3" /> Eliminar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Nombre</label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Ej. Recordatorio de pago mensual"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
                  Descripción (interna, opcional)
                </label>
                <Input
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Tipo</label>
                <select
                  value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value as BroadcastKind })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="push">Push (notificación)</option>
                  <option value="popup">Pop-up (modal)</option>
                  <option value="banner">Banner (in-app)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Título</label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  maxLength={80}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Mensaje</label>
                <textarea
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  rows={4}
                  maxLength={500}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Botón</label>
                  <Input
                    value={editing.cta_label}
                    onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">Acción</label>
                  <Input
                    value={editing.cta_action}
                    onChange={(e) => setEditing({ ...editing, cta_action: e.target.value })}
                  />
                </div>
              </div>
              {error && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="size-4" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── History ────────────────────────────────────────────────────────

function HistoryList({
  loading, broadcasts, plans, onDeleted,
}: {
  loading: boolean;
  broadcasts: BroadcastRow[];
  plans: PlanOption[];
  onDeleted: () => void;
}) {
  const profile = useAuthStore((s) => s.profile);
  const canDelete = profile?.role === 'super_admin';
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const PAGE_SIZE = 10;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return broadcasts;
    return broadcasts.filter((b) =>
      b.title.toLowerCase().includes(q) || b.body.toLowerCase().includes(q),
    );
  }, [broadcasts, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => { setPage(0); }, [query]);

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este envío del historial? Esta acción no se puede deshacer.')) return;
    setDeletingId(id);
    setError(null);
    const { error } = await supabase.from('user_broadcasts').delete().eq('id', id);
    setDeletingId(null);
    if (error) {
      setError(error.message);
      return;
    }
    onDeleted();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 size-5 animate-spin" /> Cargando…
      </div>
    );
  }
  if (broadcasts.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-md border border-dashed py-12 text-sm text-muted-foreground">
        <History className="size-4" /> Aún no se ha enviado ninguna comunicación.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por título o mensaje…"
            className="pl-8"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {filtered.length} de {broadcasts.length}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          <AlertCircle className="size-4" /> {error}
        </div>
      )}

      {pageRows.length === 0 ? (
        <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">
          Sin coincidencias para “{query}”.
        </div>
      ) : (
        <div className="space-y-2">
          {pageRows.map((b) => {
            const Icon = KIND_ICON[b.kind];
            return (
              <Card key={b.id}>
                <CardContent className="flex flex-wrap items-start justify-between gap-4 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <Icon className="size-4 text-muted-foreground" />
                      <Badge variant="outline">{KIND_LABEL[b.kind]}</Badge>
                      {b.sent_at ? (
                        <Badge className="bg-emerald-500/10 text-emerald-600">Enviado</Badge>
                      ) : (
                        <Badge className="bg-amber-500/10 text-amber-600">Pendiente</Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(b.created_at).toLocaleString('es-GT')}
                      </span>
                    </div>
                    <div className="font-medium">{b.title}</div>
                    <div className="line-clamp-2 text-sm text-muted-foreground">{b.body}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {describeAudience(b.audience, plans)}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="text-right text-sm">
                      <div className="text-2xl font-semibold">{b.recipients_count}</div>
                      <div className="text-xs text-muted-foreground">destinatarios</div>
                    </div>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(b.id)}
                        disabled={deletingId === b.id}
                        title="Eliminar (solo super-admin)"
                      >
                        {deletingId === b.id
                          ? <Loader2 className="size-4 animate-spin" />
                          : <Trash2 className="size-4 text-destructive" />}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 0 && (
        <div className="flex items-center justify-between border-t pt-3 text-sm">
          <span className="text-xs text-muted-foreground">
            Página {safePage + 1} de {totalPages} · {filtered.length} envío(s)
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft className="size-4" /> Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Siguiente <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function describeAudience(aud: Audience, plans: PlanOption[]): string {
  if (aud?.all) return 'Todos los clientes (titulares de cuenta).';
  const parts: string[] = [];
  if (aud?.roles?.length) {
    parts.push(`Roles: ${aud.roles.map((r) => ROLE_LABEL[r] ?? r).join(', ')}`);
  }
  if (aud?.plan_codes?.length) {
    const names = aud.plan_codes.map((c) => plans.find((p) => p.code === c)?.name ?? c);
    parts.push(`Planes: ${names.join(', ')}`);
  }
  if (aud?.account_ids?.length) {
    parts.push(`Cuentas: ${aud.account_ids.length} (titulares)`);
  }
  if (aud?.user_ids?.length) {
    parts.push(`Usuarios directos: ${aud.user_ids.length}`);
  }
  return parts.length ? parts.join(' · ') : 'Sin destinatarios.';
}

function TabBtn({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}
